'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { PIPELINE_COLUMNS } from '@/components/common/CustomerCardPanel';
import CustomerCardPanel from '@/components/common/CustomerCardPanel';
import type { CustomerCard, StageKey } from '@/components/common/CustomerCardPanel';

function formatCurrency(val: number): string {
  if (!val) return 'TBD';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);
}

function getStageMeta(stage: StageKey) {
  return PIPELINE_COLUMNS.find(c => c.id === stage) ?? { title: stage, color: '#6B7280' };
}

function timeAgo(dateStr: string, nowMs?: number): string {
  const base = nowMs ?? Date.now();
  const diff = base - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}


type TabId = 'catchup' | 'about' | 'activities' | 'revenue' | 'intelligence';

const TABS: { id: TabId; label: string }[] = [
  { id: 'catchup', label: 'Catch-up' },
  { id: 'about', label: 'About' },
  { id: 'activities', label: 'Activities' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'intelligence', label: 'Intelligence' },
];

interface ContactProfileProps {
  contact: CustomerCard;
  onBack: () => void;
  allContacts: CustomerCard[];
  onSelectContact: (c: CustomerCard) => void;
}

export default function ContactProfile({ contact, onBack, allContacts, onSelectContact }: ContactProfileProps) {
  const [activeTab, setActiveTab] = useState<TabId>('catchup');
  const [contactType, setContactType] = useState<string>(contact.contactType ?? '');
  const [savingContactType, setSavingContactType] = useState(false);
  const [showCustomerCard, setShowCustomerCard] = useState(false);
  const [nowMs, setNowMs] = useState<number>(0);

  // The Mailchimp contact-events feed (and the email_events fetch that backed
  // it) was removed when the email-marketing module retired in migration
  // 20260529120000. CatchupTab and ActivitiesTab now render CRM activity only.

  useEffect(() => {
    setNowMs(Date.now());
  }, []);

  useEffect(() => {
    setContactType(contact.contactType ?? '');
  }, [contact.id]);

  const handleContactTypeChange = async (value: string) => {
    setContactType(value);
    setSavingContactType(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      await supabase
        .from('customer_pipeline')
        .update({ contact_type: value || null })
        .eq('id', contact.id);
    } finally {
      setSavingContactType(false);
    }
  };

  const stageMeta = getStageMeta(contact.stage);
  const fullName = `${contact.customerFirstName} ${contact.customerLastName}`;
  const initials = `${contact.customerFirstName.charAt(0)}${contact.customerLastName.charAt(0)}`.toUpperCase();

  return (
    <div className="flex h-screen min-h-0 bg-gray-50 overflow-hidden">
      {/* ── Left Sidebar: Contact List ─────────────────────────────────────── */}
      <div className="w-64 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
        {/* Back header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 font-body text-sm font-semibold text-gray-700 hover:text-blue-600 transition-colors"
          >
            <Icon name="ChevronLeftIcon" size={16} />
            Contacts
          </button>
          <button className="flex items-center gap-1 font-body text-xs text-gray-500 hover:text-gray-700 transition-colors border border-gray-200 rounded px-2 py-1">
            Actions
            <Icon name="ChevronDownIcon" size={12} />
          </button>
        </div>

        {/* Contact list */}
        <div className="flex-1 overflow-y-auto">
          {allContacts.map(c => {
            const sm = getStageMeta(c.stage);
            const ini = `${c.customerFirstName.charAt(0)}${c.customerLastName.charAt(0)}`.toUpperCase();
            const isActive = c.id === contact.id;
            return (
              <button
                key={c.id}
                onClick={() => { onSelectContact(c); setShowCustomerCard(c.contactType === 'Customer'); }}
                className={`w-full text-left px-4 py-3 border-b border-gray-100 flex items-center gap-3 transition-colors ${
                  isActive ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                }`}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${sm.color} 0%, #000150 100%)` }}
                >
                  {ini}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-body text-sm font-semibold truncate ${isActive ? 'text-blue-700' : 'text-gray-800'}`}>
                    {c.customerFirstName} {c.customerLastName}
                  </p>
                  <p className="font-caption text-xs text-gray-400 truncate">{c.email || c.phone}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Contact header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
          <div className="flex items-start gap-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0 shadow-sm"
              style={{ background: `linear-gradient(135deg, ${stageMeta.color} 0%, #000150 100%)` }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-xl font-bold text-gray-900">{fullName}</h1>
              <p className="font-body text-sm text-gray-500 mt-0.5">{contact.address}, {contact.city}, {contact.state}</p>
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="font-body text-sm text-blue-600 hover:underline mt-0.5 block">
                  {contact.email}
                </a>
              )}
              {/* View Customer Card button — only shown when contact type is Customer */}
              {(contactType === 'Customer' || contact.contactType === 'Customer') && (
                <div className="mt-2">
                  <button
                    onClick={() => setShowCustomerCard(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white shadow-sm hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#033494' }}
                  >
                    <Icon name="IdentificationIcon" size={14} />
                    View Customer Card
                  </button>
                </div>
              )}
              {/* Action buttons */}
              <div className="flex items-center gap-2 mt-3">
                {[
                  { icon: 'PencilSquareIcon', label: 'Note' },
                  { icon: 'EnvelopeIcon', label: 'Email' },
                  { icon: 'PhoneIcon', label: 'Call' },
                  { icon: 'ClipboardDocumentListIcon', label: 'Task' },
                  { icon: 'VideoCameraIcon', label: 'Meeting' },
                ].map(btn => (
                  <button
                    key={btn.label}
                    className="flex flex-col items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-blue-300 transition-colors group"
                  >
                    <Icon name={btn.icon as any} size={16} className="text-gray-500 group-hover:text-blue-600" />
                    <span className="font-caption text-[10px] text-gray-500 group-hover:text-blue-600">{btn.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-0 mt-4 border-b border-gray-200 -mb-4">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-3 font-body text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'catchup' && <CatchupTab contact={contact} />}
          {activeTab === 'about' && <AboutTab contact={contact} />}
          {activeTab === 'activities' && <ActivitiesTab contact={contact} />}
          {activeTab === 'revenue' && <RevenueTab contact={contact} />}
          {activeTab === 'intelligence' && <IntelligenceTab contact={contact} />}
        </div>
      </div>

      {/* ── Right Panel: Key Information ──────────────────────────────────── */}
      <div className="w-72 flex-shrink-0 bg-white border-l border-gray-200 flex flex-col overflow-y-auto">
        {/* Key Information */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-bold text-gray-800">Key Information</h3>
            <button className="font-body text-xs text-blue-600 hover:underline">Actions</button>
          </div>
          <div className="space-y-3">
            {/* Contact Type Dropdown */}
            <div>
              <p className="font-caption text-[10px] text-gray-400 uppercase tracking-wider mb-1">Contact Type</p>
              <div className="relative">
                <select
                  value={contactType}
                  onChange={e => handleContactTypeChange(e.target.value)}
                  disabled={savingContactType}
                  className="w-full appearance-none font-body text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-300 transition-colors disabled:opacity-60 cursor-pointer"
                >
                  <option value="">— Select Type —</option>
                  <option value="Customer">Customer</option>
                  <option value="Realtor">Realtor</option>
                  <option value="Property Management Co">Property Management Co</option>
                  <option value="Business">Business</option>
                  <option value="Vendor">Vendor</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                  {savingContactType ? (
                    <svg className="animate-spin h-3.5 w-3.5 text-blue-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  ) : (
                    <Icon name="ChevronDownIcon" size={13} className="text-gray-400" />
                  )}
                </div>
              </div>
            </div>
            <InfoRow label="Email" value={contact.email || '—'} />
            <InfoRow label="Phone Number" value={contact.phone || '—'} isPhone />
            <InfoRow label="Street Address" value={contact.address || '—'} />
            <InfoRow label="City / State" value={`${contact.city}, ${contact.state} ${contact.zip}`} />
            <InfoRow label="Lifecycle Stage" value={stageMeta.title} color={stageMeta.color} />
            <InfoRow label="Job ID" value={contact.jobId} />
            <InfoRow label="Material" value={contact.material || '—'} />
            <InfoRow label="Payment Method" value={contact.paymentMethod || '—'} />
            <InfoRow label="Referral Source" value={contact.referralSource || '—'} />
            <InfoRow label="Financing Status" value={contact.financingStatus || '—'} />
          </div>
        </div>

        {/* Quote to Cash */}
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-bold text-gray-800">Quote to Cash</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-caption text-xs text-gray-500">Instant Estimate</span>
              <span className="font-body text-sm font-semibold" style={{ color: '#033494' }}>{formatCurrency(contact.instantEstimate)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-caption text-xs text-gray-500">Contract Value</span>
              <span className="font-body text-sm font-semibold" style={{ color: '#033494' }}>{formatCurrency(contact.contractValue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-caption text-xs text-gray-500">Total Squares</span>
              <span className="font-body text-sm font-semibold text-gray-800">{contact.totalSquares || '—'}</span>
            </div>
          </div>
        </div>

        {/* Team */}
        <div className="px-4 py-4 border-b border-gray-200">
          <h3 className="font-heading text-sm font-bold text-gray-800 mb-3">Team</h3>
          <div className="space-y-2">
            {contact.vpmName && (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-caption text-[10px] font-bold text-blue-700">
                    {contact.vpmName.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-body text-xs font-semibold text-gray-700">{contact.vpmName}</p>
                  <p className="font-caption text-[10px] text-gray-400">VPM</p>
                </div>
              </div>
            )}
            {contact.qcName && (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="font-caption text-[10px] font-bold text-green-700">
                    {contact.qcName.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-body text-xs font-semibold text-gray-700">{contact.qcName}</p>
                  <p className="font-caption text-[10px] text-gray-400">QC Inspector</p>
                </div>
              </div>
            )}
            {!contact.vpmName && !contact.qcName && (
              <p className="font-caption text-xs text-gray-400">No team assigned</p>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="px-4 py-4">
          <h3 className="font-heading text-sm font-bold text-gray-800 mb-3">Notes</h3>
          {contact.notes ? (
            <p className="font-body text-sm text-gray-600 leading-relaxed">{contact.notes}</p>
          ) : (
            <p className="font-caption text-xs text-gray-400">No notes added.</p>
          )}
        </div>
      </div>

      {/* Customer Card Panel popup */}
      {showCustomerCard && (
        <CustomerCardPanel
          card={contact}
          onClose={() => setShowCustomerCard(false)}
        />
      )}
    </div>
  );
}

// ─── Info Row ─────────────────────────────────────────────────────────────────

function InfoRow({ label, value, isPhone, color }: { label: string; value: string; isPhone?: boolean; color?: string }) {
  return (
    <div>
      <p className="font-caption text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
      {isPhone ? (
        <a href={`tel:${value}`} className="font-body text-sm font-medium" style={{ color: '#033494' }}>{value}</a>
      ) : color ? (
        <span className="font-body text-sm font-medium" style={{ color }}>{value}</span>
      ) : (
        <p className="font-body text-sm text-gray-700">{value}</p>
      )}
    </div>
  );
}

// ─── Catch-up Tab ─────────────────────────────────────────────────────────────

function CatchupTab({ contact }: { contact: CustomerCard }) {
  const stageMeta = getStageMeta(contact.stage);
  const fullName = `${contact.customerFirstName} ${contact.customerLastName}`;

  return (
    <div className="p-6 space-y-4">
      {/* AI Record Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}>
            <Icon name="SparklesIcon" size={12} className="text-white" />
          </div>
          <h3 className="font-heading text-sm font-bold text-gray-800">AI Record Summary</h3>
        </div>
        <p className="font-body text-sm text-gray-600 leading-relaxed">
          <strong>{fullName}</strong> is a roofing prospect located at {contact.address}, {contact.city}, {contact.state}.
          Currently in the <span className="font-semibold" style={{ color: stageMeta.color }}>{stageMeta.title}</span> stage
          {contact.contractValue > 0 ? ` with a contract value of ${formatCurrency(contact.contractValue)}` : ''}.
          {contact.material ? ` Interested in ${contact.material}.` : ''}
          {contact.referralSource ? ` Referred via ${contact.referralSource}.` : ''}
          {contact.notes ? ` Notes: ${contact.notes}` : ''}
        </p>
      </div>

      {/* Contact Profile Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-heading text-sm font-bold text-gray-800 mb-3">Contact Profile</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="font-caption text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Lifecycle Stage</p>
            <span
              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ background: `${stageMeta.color}18`, color: stageMeta.color }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: stageMeta.color }} />
              {stageMeta.title}
            </span>
          </div>
          <div>
            <p className="font-caption text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Payment Method</p>
            <p className="font-body text-sm text-gray-700">{contact.paymentMethod || '—'}</p>
          </div>
          <div>
            <p className="font-caption text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Material</p>
            <p className="font-body text-sm text-gray-700">{contact.material || '—'}</p>
          </div>
          <div>
            <p className="font-caption text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">Total Squares</p>
            <p className="font-body text-sm text-gray-700">{contact.totalSquares || '—'}</p>
          </div>
        </div>
      </div>

      {/* Recent Interactions — CRM activity only after Mailchimp removal. */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-heading text-sm font-bold text-gray-800 mb-3">Recent Interactions</h3>
        <div className="space-y-3">
          {[
            { icon: 'PhoneIcon', color: '#10B981', title: 'Call logged', desc: 'Discussed roofing options and timeline', time: '5 days ago' },
            { icon: 'DocumentTextIcon', color: '#F59E0B', title: 'Estimate created', desc: `Instant estimate generated for ${contact.address}`, time: '1 week ago' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}18` }}>
                <Icon name={item.icon as any} size={14} style={{ color: item.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm font-semibold text-gray-800">{item.title}</p>
                <p className="font-caption text-xs text-gray-500">{item.desc}</p>
              </div>
              <span className="font-caption text-xs text-gray-400 flex-shrink-0">{item.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── About Tab ────────────────────────────────────────────────────────────────

function AboutTab({ contact }: { contact: CustomerCard }) {
  const stageMeta = getStageMeta(contact.stage);
  return (
    <div className="p-6 space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-heading text-sm font-bold text-gray-800 mb-4">Contact Details</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <InfoRow label="First Name" value={contact.customerFirstName} />
          <InfoRow label="Last Name" value={contact.customerLastName} />
          <InfoRow label="Email" value={contact.email || '—'} />
          <InfoRow label="Phone" value={contact.phone || '—'} isPhone />
          <InfoRow label="Address" value={contact.address || '—'} />
          <InfoRow label="City" value={contact.city} />
          <InfoRow label="State" value={contact.state} />
          <InfoRow label="ZIP" value={contact.zip || '—'} />
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-heading text-sm font-bold text-gray-800 mb-4">Project Details</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <InfoRow label="Job ID" value={contact.jobId} />
          <InfoRow label="Stage" value={stageMeta.title} color={stageMeta.color} />
          <InfoRow label="Material" value={contact.material || '—'} />
          <InfoRow label="Total Squares" value={contact.totalSquares ? `${contact.totalSquares} sq` : '—'} />
          <InfoRow label="Referral Source" value={contact.referralSource || '—'} />
          <InfoRow label="Payment Method" value={contact.paymentMethod || '—'} />
          <InfoRow label="Financing Status" value={contact.financingStatus || '—'} />
          <InfoRow label="Escrow Status" value={contact.escrowStatus || '—'} />
        </div>
      </div>
    </div>
  );
}

// ─── Activities Tab ───────────────────────────────────────────────────────────

function ActivitiesTab({ contact }: { contact: CustomerCard }) {
  const fullName = `${contact.customerFirstName} ${contact.customerLastName}`;

  // Mailchimp email events + the all/email/crm filter pills retired with the
  // email-marketing module in migration 20260529120000. Only CRM activity
  // renders now; the "Log Activity" CTA is the path to add more.
  const crmActivities = [
    { icon: 'DocumentTextIcon', color: '#6366F1', type: 'Note',    title: 'Note added',       desc: contact.notes || 'No notes yet.', time: 'Today' },
    { icon: 'EnvelopeIcon',     color: '#3B82F6', type: 'Email',   title: 'Email sent',       desc: `Sent initial outreach to ${contact.email}`, time: '2 days ago' },
    { icon: 'PhoneIcon',        color: '#10B981', type: 'Call',    title: 'Call logged',      desc: 'Discussed project scope and timeline', time: '5 days ago' },
    { icon: 'ArrowRightCircleIcon', color: '#F59E0B', type: 'Stage', title: 'Stage updated', desc: `Moved to ${getStageMeta(contact.stage).title}`, time: '1 week ago' },
    { icon: 'UserPlusIcon',     color: '#8B5CF6', type: 'Created', title: 'Contact created',  desc: `${fullName} added to pipeline`, time: '2 weeks ago' },
  ];

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-heading text-sm font-bold text-gray-800">Activity Timeline</h3>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ backgroundColor: '#033494' }}>
            <Icon name="PlusIcon" size={12} />
            Log Activity
          </button>
        </div>

        <div className="divide-y divide-gray-50">
          {crmActivities.map((act, i) => (
            <div key={i} className="flex items-start gap-4 px-5 py-4">
              <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${act.color}15` }}>
                <Icon name={act.icon as any} size={16} style={{ color: act.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-body text-sm font-semibold text-gray-800">{act.title}</span>
                  <span className="font-caption text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${act.color}15`, color: act.color }}>
                    {act.type}
                  </span>
                </div>
                <p className="font-body text-sm text-gray-500 truncate">{act.desc}</p>
              </div>
              <span className="font-caption text-xs text-gray-400 flex-shrink-0 mt-0.5">{act.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Revenue Tab ──────────────────────────────────────────────────────────────

function RevenueTab({ contact }: { contact: CustomerCard }) {
  const stageMeta = getStageMeta(contact.stage);
  const progress = contact.contractValue > 0 ? Math.min(100, (contact.contractValue / 35000) * 100) : 0;

  return (
    <div className="p-6 space-y-4">
      {/* Revenue Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Instant Estimate', value: formatCurrency(contact.instantEstimate), color: '#6366F1' },
          { label: 'Contract Value', value: formatCurrency(contact.contractValue), color: '#033494' },
          { label: 'Total Squares', value: contact.totalSquares ? `${contact.totalSquares} sq` : 'TBD', color: '#10B981' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="font-caption text-xs text-gray-500 mb-1">{stat.label}</p>
            <p className="font-heading text-xl font-bold" style={{ color: stat.color }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Pipeline Progress */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-heading text-sm font-bold text-gray-800 mb-4">Pipeline Progress</h3>
        <div className="flex items-center justify-between mb-2">
          <span className="font-body text-sm text-gray-600">Current Stage</span>
          <span className="font-body text-sm font-semibold" style={{ color: stageMeta.color }}>{stageMeta.title}</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
          <div
            className="h-2 rounded-full transition-all duration-500"
            style={{ width: `${progress}%`, background: `linear-gradient(90deg, #07B0E9, #033494)` }}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <InfoRow label="Payment Method" value={contact.paymentMethod || '—'} />
          <InfoRow label="Financing Status" value={contact.financingStatus || '—'} />
          <InfoRow label="Escrow Status" value={contact.escrowStatus || '—'} />
          <InfoRow label="Referral Source" value={contact.referralSource || '—'} />
        </div>
      </div>
    </div>
  );
}

// ─── Intelligence Tab ─────────────────────────────────────────────────────────

function IntelligenceTab({ contact }: { contact: CustomerCard }) {
  const fullName = `${contact.customerFirstName} ${contact.customerLastName}`;
  const stageMeta = getStageMeta(contact.stage);

  return (
    <div className="p-6 space-y-4">
      {/* AI Intelligence Panel */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #07B0E9 0%, #033494 100%)' }}>
            <Icon name="SparklesIcon" size={14} className="text-white" />
          </div>
          <h3 className="font-heading text-sm font-bold text-gray-800">AI Intelligence</h3>
        </div>
        <div className="space-y-4">
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
            <p className="font-caption text-xs font-semibold text-blue-700 mb-1">Engagement Signal</p>
            <p className="font-body text-sm text-blue-800">
              {fullName} is in the <strong>{stageMeta.title}</strong> stage.
              {contact.contractValue > 0
                ? ` High-value prospect with ${formatCurrency(contact.contractValue)} contract.`
                : ' Early-stage lead — recommend follow-up within 48 hours.'}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-green-50 border border-green-100">
            <p className="font-caption text-xs font-semibold text-green-700 mb-1">Recommended Action</p>
            <p className="font-body text-sm text-green-800">
              {contact.stage === 'design'
                ? 'Schedule a discovery call to qualify this lead and confirm scope before measurements.'
                : contact.stage === 'measurement-pending'
                ? 'Confirm GAF measurement turnaround; chase if no report in 48 hours.'
                : contact.stage === 'estimate-ready'
                ? 'Walk the customer through the estimate and surface financing options.'
                : contact.stage === 'pending-signature'
                ? 'Follow up on contract signature — consider sending a reminder email.'
                : contact.stage === 'signed-contract'
                ? 'Hand off to onboarding: confirm install date and material delivery.'
                : contact.stage === 'pending-onboarding'
                ? 'Lock the install window and coordinate crew + permit status.'
                : contact.stage === 'onboarding-complete'
                ? 'Ready for install — schedule kickoff and QC inspection.'
                : 'Advance to next pipeline stage and update team on status.'}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
            <p className="font-caption text-xs font-semibold text-amber-700 mb-1">Risk Assessment</p>
            <p className="font-body text-sm text-amber-800">
              {contact.financingStatus === 'Pending' ?'Financing application pending — monitor approval status closely.'
                : contact.escrowStatus === 'Pending' ?'Escrow release pending — coordinate with title company.' :'No significant risk signals detected for this contact.'}
            </p>
          </div>
        </div>
      </div>

      {/* Contact Keywords */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-heading text-sm font-bold text-gray-800 mb-3">Contact Tags</h3>
        <div className="flex flex-wrap gap-2">
          {[
            contact.material,
            contact.paymentMethod,
            contact.referralSource,
            contact.stage,
            contact.city,
            contact.state,
          ].filter(Boolean).map((tag, i) => (
            <span key={i} className="font-caption text-xs px-3 py-1 rounded-full border border-gray-200 text-gray-600 bg-gray-50">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
